import React, { useCallback, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin   from '@fullcalendar/daygrid';
import timeGridPlugin  from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useQuery }    from '@tanstack/react-query';
import { Plus }        from 'lucide-react';
import dayjs           from 'dayjs';
import { fetchBookings } from '../../lib/bookingsApi';
import BookingDetailModal from '../bookings/BookingDetailModal';
import NewBookingModal    from '../bookings/NewBookingModal';

const NY_TZ = 'America/New_York';

// Convert a UTC ISO string to a "floating" local datetime string (no Z / offset)
// so FullCalendar renders it at the correct NY wall-clock time regardless of the
// browser's timezone.
function toNYFloating(iso) {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

function bookingToEvent(b) {
  const startMs = new Date(b.start_at).getTime();
  const endMs   = startMs + (b.Service?.duration_minutes || 30) * 60_000;
  return {
    id:    b.id,
    title: `${b.Client?.name || 'Client'} �� ${b.Service?.name || ''}`,
    start: toNYFloating(b.start_at),
    end:   toNYFloating(new Date(endMs).toISOString()),
    backgroundColor: b.Staff?.color || '#2E5BBA',
    borderColor:     b.Staff?.color || '#2E5BBA',
    extendedProps: { booking: b },
  };
}

export default function CalendarPage() {
  const calRef = useRef(null);
  const [range, setRange]       = useState(() => {
    const now = dayjs();
    return { from: now.startOf('month').toISOString(), to: now.endOf('month').toISOString() };
  });
  const [selected, setSelected]   = useState(null);
  const [showNew, setShowNew]     = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ['bookings', range],
    queryFn: () => fetchBookings({ from: range.from, to: range.to, limit: 500 }),
    select: d => (d.data || []).map(bookingToEvent),
  });

  const events = data || [];

  const handleDatesSet = useCallback((info) => {
    setRange({ from: info.startStr, to: info.endStr });
  }, []);

  return (
    <div className="p-4 sm:p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <div>
          <h1 className="text-lg font-semibold text-ink">Calendar</h1>
          <p className="text-sm text-ink-secondary mt-0.5">View and manage all appointments</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn btn-primary gap-2 text-sm">
          <Plus size={15} /> New booking
        </button>
      </div>

      <div className="card flex-1 overflow-hidden p-2 sm:p-4 min-h-0">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left:   'prev,next today',
            center: 'title',
            right:  'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          height="100%"
          events={events}
          datesSet={handleDatesSet}
          eventClick={({ event }) => setSelected(event.extendedProps.booking)}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={false}
          nowIndicator
          eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
          buttonText={{ today: 'Today', month: 'Month', week: 'Week', day: 'Day' }}
          dayHeaderFormat={{ weekday: 'short' }}
          views={{
            timeGridWeek: { dayHeaderFormat: { weekday: 'narrow' } },
          }}
        />
      </div>

      {selected && (
        <BookingDetailModal
          booking={selected}
          onClose={() => setSelected(null)}
          onRefresh={refetch}
        />
      )}
      {showNew && (
        <NewBookingModal onClose={() => { setShowNew(false); refetch(); }} />
      )}
    </div>
  );
}
